import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Visibility,
  Delete,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Leave, LeaveStatus, LeaveType } from '../types';
import Layout from '../components/Layout/Layout';

const Leaves: React.FC = () => {
  const navigate = useNavigate();
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
  });

  useEffect(() => {
    loadLeaves();
  }, [filters]);

  const loadLeaves = async () => {
    try {
      setLoading(true);
      setError(null);
      const filterParams: any = {};
      if (filters.status) filterParams.status = filters.status;
      if (filters.startDate) filterParams.startDate = filters.startDate.toISOString().split('T')[0];
      if (filters.endDate) filterParams.endDate = filters.endDate.toISOString().split('T')[0];

      const data = await apiService.getUserLeaves(filterParams);
      // Ensure data is always an array
      setLeaves(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load leaves:', error);
      setError('Failed to load leave requests. Please try again.');
      setLeaves([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, leave: Leave) => {
    setAnchorEl(event.currentTarget);
    setSelectedLeave(leave);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedLeave(null);
  };

  const handleDelete = async (leaveId: string) => {
    if (window.confirm('Are you sure you want to delete this leave request?')) {
      try {
        await apiService.deleteLeave(leaveId);
        // Refresh the leaves list
        loadLeaves();
      } catch (error: any) {
        console.error('Error deleting leave:', error);
        const errorMessage = error.response?.data?.message || 
                            error.response?.data?.error?.message || 
                            error.message || 
                            'Failed to delete leave request';
        setError(errorMessage);
      }
    }
  };

  const getStatusColor = (status: LeaveStatus) => {
    switch (status) {
      case LeaveStatus.APPROVED:
        return 'success';
      case LeaveStatus.REJECTED:
        return 'error';
      case LeaveStatus.PENDING:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type: LeaveType) => {
    switch (type) {
      case LeaveType.FULL_DAY:
        return 'Full Day';
      case LeaveType.HALF_DAY:
        return 'Half Day';
      case LeaveType.HOURS:
        return 'Hours';
      default:
        return type;
    }
  };

  const canEdit = (leave: Leave) => {
    return leave.status === LeaveStatus.PENDING;
  };

  const formatDateRange = (leave: Leave) => {
    const start = new Date(leave.startDate).toLocaleDateString();
    const end = new Date(leave.endDate).toLocaleDateString();
    return start === end ? start : `${start} - ${end}`;
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Leave Requests</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/leaves/new')}
        >
          Request Leave
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              size="small"
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value={LeaveStatus.PENDING}>Pending</MenuItem>
              <MenuItem value={LeaveStatus.APPROVED}>Approved</MenuItem>
              <MenuItem value={LeaveStatus.REJECTED}>Rejected</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="Start Date"
              value={filters.startDate}
              onChange={(date) => setFilters({ ...filters, startDate: date })}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePicker
              label="End Date"
              value={filters.endDate}
              onChange={(date) => setFilters({ ...filters, endDate: date })}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="outlined"
              onClick={() => setFilters({ status: '', startDate: null, endDate: null })}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Check if leaves array exists and has items */}
      {!leaves || leaves.length === 0 ? (
        <Alert severity="info">
          No leave requests found. Create your first leave request to get started.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell>Hours</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Requested</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leaves.map((leave) => (
                <TableRow key={leave.id}>
                  <TableCell>
                    <Chip
                      label={getTypeLabel(leave.type)}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDateRange(leave)}</TableCell>
                  <TableCell>
                    {leave.type === LeaveType.HOURS ? leave.hours : '-'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {leave.reason}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={leave.status}
                      color={getStatusColor(leave.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(leave.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, leave)}
                      size="small"
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            navigate(`/leaves/${selectedLeave?.id}`);
            handleMenuClose();
          }}
        >
          <Visibility sx={{ mr: 1 }} />
          View
        </MenuItem>
        {selectedLeave && canEdit(selectedLeave) && (
          <MenuItem
            onClick={() => {
              navigate(`/leaves/${selectedLeave.id}`);
              handleMenuClose();
            }}
          >
            <Edit sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {selectedLeave && selectedLeave.status === 'pending' && (
          <MenuItem
            onClick={() => {
              handleDelete(selectedLeave.id);
              setAnchorEl(null);
            }}
          >
            <Delete fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>
    </Layout>
  );
};

export default Leaves;